<template>
	<div class="vcx">
		<div v-if="isMobile" class="vcx-nav-logo-mobile vcx-flex vcx-justify-center">
			<BaseSvgIcon name="fundrise-vcx-white" width="167px" height="30px" title="VCX by Fundrise" />
		</div>
		<nav
			class="vcx-nav"
			:class="{ 'vcx-nav--mobile': isMobile }"
			aria-label="Main navigation"
			data-context="vcx-nav"
		>
			<div class="vcx-nav__container">
				<div v-if="!isMobile" class="vcx-nav__logo">
					<BaseSvgIcon name="fundrise-vcx-white" width="167px" height="36px" title="VCX by Fundrise" />
				</div>
				<div class="vcx-nav__menu">
					<BaseLink
						v-for="link in navLinks"
						:id="`nav-${link.hash}`"
						:key="link.hash"
						:link="link"
						classes="vcx-nav__link"
						@click="handleNavClick($event, link.hash)"
					/>
					<BaseButton
						classes="vcx-button-primary button vcx-px-100-smo vcx-py-25-smo"
						data-test="vcx-flyout-button"
						@click="showFlyOutMenu"
						>Learn more</BaseButton
					>
				</div>
			</div>
		</nav>
		<main id="main-content" class="vcx-landing__content">
			<!-- hero -->
			<section
				v-scrolled-into-view="MXP_SIV_HERO"
				:data-context="MXP_SIV_HERO['View ID']"
				class="vcx-section vcx-theme-dark vcx-hero"
			>
				<img :src="heroImageUrl" alt="" class="vcx-hero__image" fetchpriority="high" aria-hidden="true" />
				<div class="vcx-container vcx-hero__container">
					<div class="vcx-grid">
						<div class="vcx-col-12 vcx-col-8-md vcx-col-offset-2-md vcx-col-6-lg vcx-col-offset-3-lg vcx-text-center vcx-pb-100">
							<h1 class="vcx-h0 vcx-h2-smo vcx-mb-150 vcx-mb-100-smo">
								The public ticker <br v-if="isMobile" />for private&nbsp;tech<sup
									class="vcx-hero__superscript vcx-body-lg vcx-body-sm-smo vcx-font-weight-regular"
									>SM</sup
								>
							</h1>
							<p class="vcx-body-xl vcx-body-md-smo vcx-text-secondary vcx-mb-400 vcx-mb-225-smo">
								VCX is a NYSE-listed public venture fund that gives you access to top private tech
								companies.
							</p>
							<VcxLeadCapture />
						</div>
					</div>
				</div>
			</section>
			<VcxLandingDemocratizingSection
				v-scrolled-into-view="MXP_SIV_DEMOCRATIZING"
				:data-context="MXP_SIV_DEMOCRATIZING['View ID']"
			/>
			<section
				id="about-vcx"
				v-scrolled-into-view="MXP_SIV_ABOUT_VCX"
				:data-context="MXP_SIV_ABOUT_VCX['View ID']"
				class="vcx-section vcx-theme-dark"
			>
				<div class="vcx-container">
					<h2 class="vcx-h2">About VCX</h2>
					<div class="vcx-grid vcx-mt-225 vcx-mt-500-md">
						<div v-if="!isMobile" data-test="vcx-about-overview" class="vcx-col-12 vcx-col-7-md">
							<h3 class="vcx-h5 vcx-mb-200">Overview</h3>
							<p class="vcx-text-secondary vcx-body-lg">
								The Fundrise Innovation Fund (Ticker: VCX) is designed to deliver long-term growth from
								a diversified portfolio of technology companies. The fund is focused on investing in
								potentially high-growth private technology businesses.
							</p>
							<p class="vcx-text-secondary vcx-body-lg">
								VCX takes a “multi-stage” approach, investing across the lifecycle of a private company
								– from early to late stage and continuing to hold after any potential IPOs. The fund
								invests across thematic sectors including artificial intelligence (AI) and machine
								learning (ML), data infrastructure, vertical and horizontal software, and other
								categories with attractive long-term growth potential.
							</p>
							<p>
								<BaseLink
									class="vcx-link"
									:link="{ router: 'home', text: 'Learn more about Fundrise' }"
								></BaseLink>
							</p>
						</div>
						<div class="vcx-col-12 vcx-col-5-md">
							<div class="vcx-card vcx-card-dark">
								<div class="vcx-label-md vcx-text-tertiary">Fund Details</div>
								<table class="vcx-about-the-fund-table vcx-body-lg vcx-mt-100">
									<tbody>
										<tr>
											<td>Ticker</td>
											<td class="vcx-text-right">VCX</td>
										</tr>
										<tr>
											<td>Listing venue</td>
											<td class="vcx-text-right">NYSE</td>
										</tr>
										<tr>
											<td>Advisor</td>
											<td class="vcx-text-right">Fundrise Advisors</td>
										</tr>
										<tr>
											<td>Inception date</td>
											<td class="vcx-text-right">July 2022</td>
										</tr>
										<tr>
											<td>Annual management fee</td>
											<td class="vcx-text-right">1.85%</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
					<p v-if="isMobile" class="vcx-mt-200">
						<BaseLink
							class="vcx-link"
							:link="{ router: 'home', text: 'Learn more about Fundrise' }"
						></BaseLink>
					</p>
				</div>
			</section>
			<!-- portfolio -->
			<section
				id="portfolio"
				v-scrolled-into-view="MXP_SIV_PORTFOLIO"
				:data-context="MXP_SIV_PORTFOLIO['View ID']"
				:class="['vcx-section', 'vcx-theme-dark', { 'vcx-portfolio--mobile': isMobile }]"
			>
				<div class="vcx-container">
					<h2 class="vcx-h1 vcx-mb-150">Portfolio</h2>
					<VcxPortfolioTables
						:is-loading="isSanityLoading"
						:portfolio-as-of-date="vcxAsOfDate"
						:portfolio-description="vcxPortfolioDescription"
						:portfolio-holdings="vcxPortfolioHoldings"
						:sector-breakdown="vcxSectorBreakdown"
						:private-public-breakdown="vcxPrivatePublicBreakdown"
						:disclaimer="vcxPortfolioSectionDisclaimer"
					/>
				</div>
			</section>
			<!-- performance -->
			<section
				id="performance"
				v-scrolled-into-view="MXP_SIV_PERFORMANCE"
				:data-context="MXP_SIV_PERFORMANCE['View ID']"
				class="vcx-section vcx-theme-dark"
			>
				<div class="vcx-container">
					<h2 class="vcx-h1 vcx-mb-225 vcx-mb-500-md">Performance</h2>
					<VcxPerformanceSection
						:is-loading="isSanityLoading"
						:performance-metrics="vcxPerformanceMetrics"
						:performance-disclaimer="vcxPerformanceDisclaimer"
					/>
				</div>
			</section>
			<!-- podcast -->
			<VcxLandingPodcast
				v-scrolled-into-view="MXP_SIV_PODCAST"
				:data-context="MXP_SIV_PODCAST['View ID']"
				:podcasts="vcxFeaturedPodcasts"
			/>
			<!-- literature -->
			<section
				v-scrolled-into-view="MXP_SIV_LITERATURE"
				:data-context="MXP_SIV_LITERATURE['View ID']"
				class="vcx-section vcx-theme-dark"
			>
				<div class="vcx-container">
					<h2 class="vcx-h4 vcx-mb-100">Literature</h2>
					<p class="vcx-body-lg vcx-text-secondary vcx-mb-400">
						All investors should consider the investment objectives, risks and charges and expenses of the
						Fund carefully before investing. Information regarding such considerations, including the
						prospectus of the ‘40 Act registered fund may be found below. Investors should read the
						prospectus carefully before investing.
					</p>
					<BasePlaceHolder :placehold="isLoading" :section-count="4" :line-count="2" theme="dark">
						<VcxLiteratureLinkGrid class="display-block" :literature-list="vcxLiteratureItems" />
					</BasePlaceHolder>
				</div>
			</section>
			<!-- cta -->
			<section
				v-scrolled-into-view="MXP_SIV_CTA"
				:data-context="MXP_SIV_CTA['View ID']"
				class="vcx-section vcx-theme-dark"
			>
				<div class="vcx-container">
					<div class="vcx-cta-section vcx-grid">
						<h2 class="vcx-col-12 vcx-col-6-md vcx-col-7-lg vcx-col-6-xxl vcx-h3 vcx-text-gray-30">
							Sign up to receive updates about VCX
						</h2>
						<VcxLeadCapture
							class="vcx-col-12 vcx-col-6-md vcx-col-offset-6-md vcx-col-5-lg vcx-col-offset-7-lg"
						/>
					</div>
				</div>
			</section>
		</main>
		<footer
			v-scrolled-into-view="MXP_SIV_FOOTER"
			:data-context="MXP_SIV_FOOTER['View ID']"
			class="vcx-container vcx-body-md vcx-text-gray-30"
		>
			<hr class="vcx-divider-dark vcx-mb-600" />
			<BasePlaceHolder :placehold="isSanityLoading" :section-count="5" :line-count="3" theme="dark">
				<RichTextComponent :blocks="vcxFooterDisclaimer" />
			</BasePlaceHolder>
		</footer>
		<!-- Flyout Menu -->
		<VcxFlyOut v-model="showFlyOut" />
	</div>
</template>

<script setup lang="ts">
import '../../styles/vcx/vcx.scss';
import { useHead } from '@unhead/vue';
import { computed, onMounted, Ref, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import RichTextComponent from '@components/sanity/rich-text-component.vue';
import VcxFlyOut from '@components/vcx/vcx-fly-out.vue';
import VcxLandingDemocratizingSection from '@components/vcx/vcx-landing-democratizing-section.vue';
import VcxLandingPodcast from '@components/vcx/vcx-landing-podcast.vue';
import VcxLeadCapture from '@components/vcx/vcx-lead-capture.vue';
import VcxLiteratureLinkGrid from '@components/vcx/vcx-literature-link-grid.vue';
import VcxPerformanceSection from '@components/vcx/vcx-performance-section.vue';
import VcxPortfolioTables from '@components/vcx/vcx-portfolio-tables.vue';
import { appCloudfrontPath } from '@constants';
import { growthTechReitId } from '@constants/reit';
import {
	MXP_SIV_ABOUT_VCX,
	MXP_SIV_CTA,
	MXP_SIV_DEMOCRATIZING,
	MXP_SIV_FOOTER,
	MXP_SIV_HERO,
	MXP_SIV_LITERATURE,
	MXP_SIV_PERFORMANCE,
	MXP_SIV_PODCAST,
	MXP_SIV_PORTFOLIO
} from '@constants/vcx';
import { getVcxLandingPage } from '@sanity/graphql/vcx-landing-page';
import { getFund } from '@sanity/proxy/funds';
import { useAppStore } from '@stores/app';
import { Fund, FundLiterature } from 'types/funds';
import { Link } from 'types/layout';
import { SanityRichTextBlock } from 'types/sanity';
import { SanityKeyValueData } from 'types/sanity/charts-data';
import { SanityFeaturedPodcast } from 'types/sanity/vcx-landing-page';

const metaObject = {
	title: 'VCX by Fundrise',
	titleTemplate: null,
	meta: [
		{ property: 'og:title', content: 'VCX by Fundrise' },
		{
			name: 'description',
			content: `VCX: The public ticker for private tech`
		},
		{
			property: 'og:description',
			content: `VCX: The public ticker for private tech`
		},
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: 'https://getvcx.com/' },
		{
			property: 'og:image',
			content: `${appCloudfrontPath}/images/vcx/vcx-meta.png`
		}
	],
	script: [`window.prerenderReady = false;`]
};

const headControl = useHead(metaObject);

const appStore = useAppStore();
const router = useRouter();
const route = useRoute();
const isLoading = ref(true);
const isSanityLoading = ref(true);

const vcxAsOfDate: Ref<string> = ref('');
const vcxPerformanceMetrics: Ref<Array<SanityKeyValueData>> = ref([]);
const showFlyOut = ref(false);
const vcxPerformanceDisclaimer: Ref<string> = ref('');
const vcxPortfolioDescription: Ref<Array<SanityRichTextBlock>> = ref([]);
const vcxPortfolioHoldings: Ref<Array<SanityKeyValueData>> = ref([]);
const vcxSectorBreakdown: Ref<Array<SanityKeyValueData>> = ref([]);
const vcxPrivatePublicBreakdown: Ref<Array<SanityKeyValueData>> = ref([]);
const vcxPortfolioSectionDisclaimer: Ref<string> = ref('');
const vcxFeaturedPodcasts: Ref<Array<SanityFeaturedPodcast>> = ref([]);
const vcxLiteratureItems: Ref<Array<FundLiterature>> = ref([]);
const vcxFooterDisclaimer: Ref<Array<SanityRichTextBlock>> = ref([]);

const navLinks: Array<Link> = [
	{ href: '#about-vcx', text: 'About VCX', hash: '#about-vcx' },
	{ href: '#portfolio', text: 'Portfolio', hash: '#portfolio' },
	{ href: '#performance', text: 'Performance', hash: '#performance' }
];

function handleNavClick(event: Event, hash: string | undefined): void {
	event.preventDefault();
	if (!hash) return;
	router.replace({ hash: `${hash}` });
}
const heroImageUrl = computed(() => `${appCloudfrontPath}/images/vcx/vcx-hero.webp`);
const isMobile = computed(() => appStore.isMobile);

const fund: Ref<Fund | null> = ref(null);

function showFlyOutMenu() {
	showFlyOut.value = true;
}

async function fetchSanityContent() {
	try {
		const vcxLandingPage = await getVcxLandingPage();
		if (vcxLandingPage) {
			vcxAsOfDate.value = vcxLandingPage.vcxFundDetailsUpdateAsOfDate ?? '';
			vcxPerformanceMetrics.value = vcxLandingPage.vcxPerformanceData;
			vcxPerformanceDisclaimer.value = vcxLandingPage.vcxPerformanceDisclaimer ?? '';
			vcxPortfolioDescription.value = vcxLandingPage.vcxPortfolioDescription;
			vcxPortfolioHoldings.value = vcxLandingPage.vcxPortfolioHoldings;
			vcxSectorBreakdown.value = vcxLandingPage.vcxSectorBreakdown;
			vcxPrivatePublicBreakdown.value = vcxLandingPage.vcxPrivatePublicBreakdown;
			vcxPortfolioSectionDisclaimer.value = vcxLandingPage.vcxPortfolioSectionDisclaimer;
			vcxFeaturedPodcasts.value = vcxLandingPage.vcxFeaturedPodcasts;
			vcxFooterDisclaimer.value = vcxLandingPage.vcxFlatFooterDisclaimer;
		}
	} finally {
		isSanityLoading.value = false;
	}
}

async function fetchFundData() {
	try {
		fund.value = await getFund(growthTechReitId, { include: ['SIMULATED_RETURNS'] });
		vcxLiteratureItems.value = fund.value?.literature ?? [];
	} finally {
		isLoading.value = false;
	}
}

onMounted(() => {
	if (route.query.invest === 'true') {
		showFlyOutMenu();
	}
});

function setup() {
	Promise.all([fetchSanityContent(), fetchFundData()]).finally(() => {
		headControl?.patch({
			...metaObject,
			script: [`window.prerenderReady = true;`]
		});
	});
}

setup();
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive.scss' as *;
.vcx {
	min-height: 100vh;
	background-color: var(--vcx-bg-dark);
	font-family: var(--vcx-font-body), serif;
	color: var(--vcx-content-inverse);
}

.vcx-nav-logo-mobile {
	background-color: var(--vcx-bg-dark);
	padding: var(--vcx-space-150) var(--vcx-space-150) 0 var(--vcx-space-150);
}

.vcx-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	backdrop-filter: blur(32px);
	transition:
		background-color var(--vcx-transition-base),
		box-shadow var(--vcx-transition-base);

	&--mobile {
		position: sticky;
		backdrop-filter: none;
		background-color: var(--vcx-bg-dark);
		border-bottom: 1px solid var(--vcx-gray-70);
	}

	&__container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: var(--vcx-max-width);
		margin: 0 auto;
		padding: var(--vcx-space-150) var(--vcx-space-300);

		.vcx-nav--mobile & {
			padding: var(--vcx-space-100) var(--vcx-space-100);
		}
	}

	&__menu {
		display: flex;
		align-items: center;
		gap: var(--vcx-space-200);

		.vcx-nav--mobile & {
			width: 100%;
			gap: var(--vcx-space-50);
			justify-content: space-between;
		}
	}

	&__link {
		font-size: var(--vcx-font-size-body-lg);
		font-weight: var(--vcx-font-weight-regular);
		line-height: var(--vcx-line-height-body-lg);
		color: var(--vcx-gray-white);
		text-decoration: none;
		transition:
			opacity var(--vcx-transition-fast),
			color var(--vcx-transition-base),
			transform var(--vcx-transition-base);

		&:hover {
			transform: translateY(-2px);
			opacity: 0.8;
		}

		.vcx-nav--mobile & {
			font-size: var(--vcx-font-size-body-md);
		}
	}

	&--scrolled {
		background-color: var(--vcx-bg-dark);
		box-shadow: var(--vcx-shadow-md);
	}

	.vcx-button-primary {
		height: auto;

		@include vcx-media-smo {
			font-size: var(--vcx-font-size-body-sm);
		}

		&:focus,
		&:focus-within {
			background-color: var(--vcx-brand-primary);
			color: var(--vcx-content-primary);
		}

		&:hover:not(:disabled) {
			background-color: rgba(244, 99, 58, 0.75);
			color: var(--vcx-content-primary);
		}

		&:focus-visible {
			outline: 2px solid var(--vcx-brand-primary);
			outline-offset: 2px;
			background-color: var(--vcx-brand-primary);
			color: var(--vcx-content-primary);
		}

		.vcx-nav--mobile & {
			font-size: var(--vcx-font-size-body-lg);
		}
	}
}

table.vcx-about-the-fund-table {
	width: 100%;
	tbody {
		tr td {
			padding: var(--vcx-space-100) 0;
		}
		tr:first-of-type td {
			padding-top: var(--vcx-space-50);
		}
		tr:last-of-type td {
			padding-bottom: var(--vcx-space-50);
		}
	}

	tr + tr {
		border-top: 1px solid var(--vcx-content-secondary);
	}
}

.vcx-landing__content {
	padding-top: 78px;

	.vcx-nav--mobile ~ & {
		padding-top: 0;
	}
}

.vcx-hero {
	position: relative;
	padding-bottom: 5rem;

	@include vcx-media-smo {
		padding-top: var(--vcx-space-600);
		padding-bottom: var(--vcx-space-600);
	}

	@include vcx-media-lg {
		padding-bottom: 10rem;
	}

	&__superscript {
		top: -1.6rem;

		@include vcx-media-smo {
			top: -0.75rem;
		}
	}

	&__image {
		position: absolute;
		bottom: 0;
		left: 50%;
		transform: translate(-50%, 35%);
		width: 100%;
		height: 40vh;
		object-fit: cover;
		object-position: bottom center;
		pointer-events: none;
		z-index: 0;

		@include vcx-media-xl {
			height: 80vh;
		}

		@include vcx-media-xxxl {
			height: 90vh;
			object-position: bottom center;
		}
	}

	&__container {
		position: relative;
		z-index: 1;
	}
}

.vcx-portfolio--mobile {
	background-color: var(--vcx-gray-95);
}

.vcx footer {
	padding-bottom: var(--vcx-space-600);
	ol {
		margin-top: var(--vcx-space-150);
		margin-left: var(--vcx-space-150);
		list-style: decimal;
		li {
			display: list-item;
		}
	}
	:deep(.text-link) {
		color: var(--vcx-content-inverse);
		text-decoration: underline;
		text-underline-offset: 2px;
		&:hover {
			color: var(--vcx-content-inverse-secondary);
		}
	}
}
</style>
